import CreativeType from '@/Enums/Creatives/CreativeType.enum';
import ScreenKey from '@/Enums/Screens/ScreenKey.enum';
import { IParsedAdvertiser } from '@/Interfaces/Advertiser.interface';
import {
  ICreateDV360TicketsCreativePayload,
  IDV360TicketsCreative,
  IDV360TicketsCreativeFormData,
  IUpdateDV360TicketsCreativePayload,
} from '@/Interfaces/DV360Tickets/Creative.interface';
import CloseButton from '@/components/Modal/common/CloseButton';
import ModalBody from '@/components/Modal/common/ModalBody';
import InputSkeleton from '@/components/Skeleton/InputSkeleton';
import { CREATIVE_TYPES_ICONS, CREATIVE_TYPES_TITLES } from '@/constants/creativeLibrary';
import { dv360creativeFormDataToApiStructure, dv360creativeToFormData } from '@/helpers/dv360Tickets/creatives';
import { getFieldErrorProps } from '@/helpers/form';
import CreativesModalButtons from '@/pages/CampaignManagement/CreateCampaign/LineItemForm/Creatives/Modal/components/CreativesModalButtons';
import { ICreativeFormModalDefaults } from '@/pages/CreativeLibrary/Modals/CreativeFormModal';
import FileUpload from '@/pages/CreativeLibrary/Modals/components/FileUpload';
import MapItemButton from '@/pages/CreativeLibrary/Modals/components/MapItemButton';
import { dv360DefaultFormData } from '@/pages/CreativeLibrary/Modals/constants';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectMeScreenAccess } from '@/store/selectors/me.selectors';
import { selectGoogleAdvertisers } from '@/store/selectors/reportingDashboard/shared.selectors';
import { createDV360Creative, updateDV360Creative } from '@/store/slices/dv360Tickets/creatives.slice';
import { DV360CreativeType } from '@/types/creativeLibrary';
import { CLICK_THROUGH_URL_RULES, YOUTUBE_URL_RULES } from '@/utils/validationRules';
import { ContentCopy } from '@mui/icons-material';
import { Autocomplete, FormLabel, IconButton, Modal, TextField, Tooltip } from '@mui/material';
import { type ChangeEvent, type MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';

interface IProps {
  open: boolean;
  handleClose: (refresh?: boolean, duration?: number) => void;
  creativeData?: IDV360TicketsCreative;
  defaults?: ICreativeFormModalDefaults;
}

const DV360CreativeFormModal = ({ open, handleClose, creativeData, defaults }: IProps) => {
  const {
    getValues,
    control,
    formState: { isValid },
    setValue,
    reset,
    resetField,
    trigger,
  } = useForm<IDV360TicketsCreativeFormData>({
    defaultValues: dv360DefaultFormData,
    mode: 'onChange',
  });

  const dispatch = useAppDispatch();

  const googleAdvertisers = useAppSelector(selectGoogleAdvertisers);

  const advertisers = useMemo(
    () =>
      defaults?.advertiser
        ? [defaults.advertiser]
        : googleAdvertisers.filter((item): item is IParsedAdvertiser<number> => item !== undefined),
    [googleAdvertisers, defaults],
  );

  const [loading, setLoading] = useState<boolean>(false);
  const readOnly = useAppSelector(selectMeScreenAccess(ScreenKey.CREATIVE_LIBRARY));
  const [openCopyTooltip, setOpenCopyTooltip] = useState<boolean>(false);

  const [watchType, watchFile, watchVideoUrl] = useWatch({ control, name: ['type', 'file', 'videoUrl'] });

  useEffect(() => {
    if (defaults?.advertiser) {
      const defaultType = defaults.types[0];

      reset({
        ...dv360DefaultFormData,
        advertiserId: defaults.advertiser.value,
        type:
          defaultType === CreativeType.VIDEO || defaultType === CreativeType.YOUTUBE_LINK
            ? defaultType
            : CreativeType.VIDEO,
      });
    }
  }, [defaults, reset]);

  useEffect(() => {
    if (creativeData) {
      reset(dv360creativeToFormData(creativeData));

      if (!creativeData.clickUrl) {
        trigger('clickUrl');
      }
    }
  }, [creativeData, reset, trigger]);

  const handleTypeChange = useCallback(
    ({ target: { id } }: ChangeEvent<HTMLInputElement>) => {
      setValue('type', id as DV360CreativeType);
      resetField('videoUrl');
      resetField('file');
    },
    [setValue, resetField],
  );

  const _handleClose = useCallback(
    (refresh?: boolean, duration?: number) => {
      reset(dv360DefaultFormData);
      handleClose(refresh, duration);
    },
    [reset, handleClose],
  );

  const handleSave = useCallback(async () => {
    const formData = getValues();

    setLoading(true);

    try {
      const data = dv360creativeFormDataToApiStructure(formData, creativeData?.id);
      let duration;
      if (creativeData?.id) {
        await dispatch(updateDV360Creative(data as IUpdateDV360TicketsCreativePayload)).unwrap();
      } else {
        const creative = await dispatch(createDV360Creative(data as ICreateDV360TicketsCreativePayload)).unwrap();
        duration = creative.duration;
      }
      toast.success(`Creative ${creativeData?.id ? 'updated' : 'created'} successfully`);
      _handleClose(true, duration || undefined);
    } catch (err) {
      toast.error(`Error ${creativeData?.id ? 'updating' : 'creating'} creative: ${(err as Error).message}`);
    }

    setLoading(false);
  }, [getValues, creativeData?.id, dispatch, _handleClose]);

  const setUploadedFile = useCallback(
    (file: File) => {
      setValue('file', file);
    },
    [setValue],
  );

  const isSaveDisabled = useMemo(() => {
    if (creativeData) {
      return false;
    }

    if (watchType === CreativeType.VIDEO) {
      return !watchFile;
    }

    if (watchType === CreativeType.YOUTUBE_LINK) {
      return !watchVideoUrl;
    }

    return false;
  }, [watchType, watchFile, watchVideoUrl, creativeData]);

  const onCopyClick = useCallback(
    async (e: MouseEvent) => {
      e.stopPropagation();
      if (watchVideoUrl) {
        await navigator.clipboard.writeText(watchVideoUrl);
        setOpenCopyTooltip(true);
        setTimeout(() => setOpenCopyTooltip(false), 700);
      }
    },
    [watchVideoUrl],
  );

  return (
    <Modal open={open}>
      <ModalBody className='!w-[740px]'>
        <p className='pl-4 pt-2 mb-6 text-xl font-semibold'>{creativeData ? 'Edit Creative' : 'Add New Creative'}</p>
        <div className='absolute right-[24px] top-[24px]'>
          <CloseButton onClick={_handleClose} />
        </div>
        <form className='mx-4 flex flex-col gap-y-4'>
          <div className='flex flex-col w-full'>
            <FormLabel className='text-sm font-semibold mb-2' htmlFor='creative-name-input'>
              Creative Name*
            </FormLabel>
            {loading ? (
              <InputSkeleton />
            ) : (
              <Controller
                name='name'
                rules={{ required: { value: true, message: 'Creative Name is required' } }}
                control={control}
                disabled={readOnly}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    placeholder='Enter Creative Name'
                    {...getFieldErrorProps(fieldState)}
                    id='creative-name-input'
                  />
                )}
              />
            )}
          </div>
          <div className='flex flex-col w-full'>
            <FormLabel className='text-sm font-semibold mb-2'>Type</FormLabel>
            <div className='flex flex-wrap gap-x-2 gap-y-2'>
              {[CreativeType.YOUTUBE_LINK, CreativeType.VIDEO].map(type => (
                <MapItemButton
                  key={type}
                  disabled={loading || !!creativeData || readOnly}
                  label={CREATIVE_TYPES_TITLES[type]}
                  Icon={CREATIVE_TYPES_ICONS[type]}
                  checked={watchType === type}
                  onChange={handleTypeChange}
                  id={type}
                />
              ))}
            </div>
          </div>
          <div className='flex flex-col w-full'>
            <FormLabel className='text-sm font-semibold mb-2' htmlFor='advertiser-select'>
              Advertiser*
            </FormLabel>
            {loading ? (
              <InputSkeleton />
            ) : (
              <Controller
                name='advertiserId'
                rules={{ required: { value: true, message: 'Advertiser is required' } }}
                control={control}
                disabled={readOnly}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    {...field}
                    value={advertisers.find(adv => adv.value === field.value) || null}
                    renderInput={props => (
                      <TextField
                        {...props}
                        sx={{
                          '& .MuiInputBase-input': {
                            fontWeight: '400',
                          },
                        }}
                        placeholder='Select Advertiser'
                        {...getFieldErrorProps(fieldState)}
                      />
                    )}
                    getOptionLabel={option => option.label}
                    getOptionKey={option => option.value}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    onChange={(_, value) => field.onChange(value?.value || null)}
                    options={advertisers}
                    loading={false}
                    disabled={!!defaults?.advertiser || readOnly}
                    id='advertiser-select'
                  />
                )}
              />
            )}
          </div>
          {!creativeData && (
            <>
              {watchType === CreativeType.YOUTUBE_LINK && (
                <div className='flex flex-col w-full'>
                  <FormLabel className='text-sm font-semibold mb-2' htmlFor='youtube-video-url-input'>
                    Youtube Video URL*
                  </FormLabel>
                  <FormLabel className='text-xs'>
                    <span className='text-gray-500'>
                      The video must be set to Public in order to be used as a creative
                    </span>
                  </FormLabel>
                  {loading ? (
                    <InputSkeleton />
                  ) : (
                    <Controller
                      name='videoUrl'
                      rules={YOUTUBE_URL_RULES}
                      control={control}
                      disabled={readOnly}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          fullWidth
                          placeholder='Enter Youtube Video URL'
                          {...getFieldErrorProps(fieldState)}
                          id='youtube-video-url-input'
                        />
                      )}
                    />
                  )}
                </div>
              )}
              {watchType === CreativeType.VIDEO && (
                <>
                  <FormLabel className='text-sm font-semibold mb-2'>Upload Creative</FormLabel>
                  <FileUpload
                    key={watchType}
                    format={watchType}
                    setUploadedFile={setUploadedFile}
                    loading={loading}
                    failed={false}
                    disabled={readOnly}
                  />
                </>
              )}
            </>
          )}
          {!!creativeData && watchVideoUrl && (
            <div className='flex flex-col w-ful overflow-hidden'>
              <FormLabel className='text-sm font-semibold mb-2'>Video Url</FormLabel>
              <div className='flex overflow-hidden'>
                <a
                  href={watchVideoUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='overflow-hidden text-ellipsis whitespace-nowrap text-blue-500 hover:underline'
                  title={watchVideoUrl}
                >
                  {watchVideoUrl}
                </a>
                <Tooltip title='Copied' open={openCopyTooltip} placement='top'>
                  <IconButton aria-label='copy' onClick={onCopyClick} size='small' className='ml-1'>
                    <ContentCopy sx={{ width: '14px', height: '14px' }} />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          )}
          <div className='flex flex-col w-full'>
            <FormLabel className='text-sm font-semibold mb-2' htmlFor='click-through-url-input'>
              Click Through URL*
            </FormLabel>
            {loading ? (
              <InputSkeleton />
            ) : (
              <Controller
                name='clickUrl'
                rules={CLICK_THROUGH_URL_RULES}
                control={control}
                disabled={readOnly}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    placeholder='Enter Click Through URL'
                    {...getFieldErrorProps(fieldState)}
                    id='click-through-url-input'
                  />
                )}
              />
            )}
          </div>
          <CreativesModalButtons
            handleClose={_handleClose}
            handleSave={handleSave}
            loading={loading}
            saveButtonTitle={creativeData ? 'Save' : 'Create'}
            saveButtonLoadingTitle={creativeData ? 'Saving' : 'Creating'}
            isSaveDisabled={!isValid || isSaveDisabled || readOnly}
          />
        </form>
      </ModalBody>
    </Modal>
  );
};

export default DV360CreativeFormModal;
